import { toast } from 'react-toastify';
import React from 'react';
import { isUndefined, noop, remove } from 'lodash';
import uniqid from 'uniqid';
import { ToastContent } from '../components';
import styles from '../Toasts.module.scss';
import * as types from '../constants/toastTypes';
import CloseToast from 'modules/Toasts/components/CloseToast';

const DEFAULT_GROUP = 'TOASTS_COMMON';

/**
 * @typedef {Object} ToastOptions
 * @property {function} [onClose] - Called when toast is unmounted.
 * @property {function} [onOpen] - Called when toast is mounted.
 * @property {React.CSSProperties} [style] - An optional inline style to apply.
 * @property {string | number} [toastId] - Set a custom `toastId`
 * @property {string | number} [updateId] - Used during update
 * @property {number} [delay] - Add a delay in ms before the toast appear.
 * @property { number | string} [progress] - Select option label
 * @property { React.ReactElement } [closeButton] - Select option label
 * @property { (number|boolean) } [autoClose=5000] - Select option label
 * @property { string } [group] - notification group
 */

/**
 * Toast wrapper service
 */
class ToastService {
  toastsGroups = {};

  _getClassNamesOptions(type) {
    return {
      className: `${styles.toast} ${this._getClassName(type)}`,
      bodyClassName: '',
      progressClassName: '',
    };
  }

  _getClassName(type) {
    const notificationClassNames = {
      [types.NOTIFICATION]: styles.notify,
      [types.ERROR]: styles.error,
      [types.CUSTOM]: styles.custom,
    };

    return notificationClassNames[type] || '';
  }

  _getOptions(options) {
    const defaultOptions = {
      closeButton: React.createElement(CloseToast),
      hideProgressBar: true,
    };

    return {
      ...defaultOptions,
      ...options,
    };
  }

  _addToGroup(id, group = DEFAULT_GROUP) {
    if (!this.toastsGroups[group]) {
      this.toastsGroups[group] = [];
    }

    this.toastsGroups[group].push(id);
  }

  _releaseFromGroup(id, group = DEFAULT_GROUP) {
    if (this.toastsGroups[group]) {
      this.toastsGroups[group] = remove(this.toastsGroups[group], id);
    }
  }

  /**
   *
   * @param {function} originalFn
   * @param toastId
   * @param {string} group - Toast group
   * @private
   */
  _decorateWithGroupRelease(originalFn = noop, toastId, group) {
    const releaseFromGroup = this._releaseFromGroup.bind(this, toastId, group);
    return function _decoratedOnClose(...args) {
      releaseFromGroup();
      originalFn.apply(this, args);
    };
  }

  /**
   * Show default notification
   * @param {string} title
   * @param {string} [details]
   * @param {ToastOptions} [optionsObj]
   * @param {function} [optionsObj.onClose] - onClose handler
   * @param {string} [optionsObj.group] - toast group
   * @param {string} [optionsObj.toastId] - toast id
   */
  notify(title, details, { onClose, group, toastId = uniqid(), onClick, ...options } = {}) {
    toast(
      React.createElement(ToastContent, { title, details, onClick }),
      this._getOptions({
        ...this._getClassNamesOptions(types.NOTIFICATION),
        ...options,
        onClose: this._decorateWithGroupRelease(onClose, toastId, group),
        toastId,
      }),
    );
  }

  /**
   * Show error notification
   * @param {string} title
   * @param {string} [details]
   * @param {ToastOptions} [optionsObj]
   * @param {function} [optionsObj.onClose] - onClose handler
   * @param {string} [optionsObj.group] - toast group
   * @param {string} [optionsObj.toastId] - toast id
   */
  error(title, details, { onClose, group, toastId = uniqid(), onClick, ...options } = {}) {
    toast(
      React.createElement(ToastContent, { title, details, onClick }),
      this._getOptions({
        ...this._getClassNamesOptions(types.ERROR),
        ...options,
        onClose: this._decorateWithGroupRelease(onClose, toastId, group),
        toastId,
      }),
    );

    this._addToGroup(toastId, group);
  }

  /**
   * Show custom notification
   * @param CustomComponent
   * @param {ToastOptions} [optionsObj]
   * @param {function} [optionsObj.onClose] - onClose handler
   * @param {string} [optionsObj.group] - toast group
   * @param {string} [optionsObj.toastId] - toast id
   */
  custom(CustomComponent, { onClose, group, toastId = uniqid(), ...options }) {
    toast(
      CustomComponent,
      this._getOptions({
        ...this._getClassNamesOptions(types.CUSTOM),
        ...options,
        onClose: this._decorateWithGroupRelease(onClose, toastId, group),
        toastId,
      }),
    );
  }

  /**
   *
   * @param type - Message type
   * @param title - message body or title (is details exist)
   * @param details* - message details
   * @param {ToastOptions} [options]
   * @param {function} [options.onClose] - onClose handler
   * @param {string} [options.group] - toast group
   * @param {string} [options.toastId] - toast id
   * @returns {*}
   */
  show(type, title, details, options) {
    const notificationRenders = {
      [types.NOTIFICATION]: this.notify.bind(this),
      [types.ERROR]: this.error.bind(this),
    };
    const notificationRenderer =
      notificationRenders[type] || notificationRenders[types.NOTIFICATION];

    notificationRenderer(title, details, options);
  }

  /**
   * Dismiss toast(all toasts if no id is presented)
   * @param {string} [toastId]
   */
  dismiss(toastId) {
    toast.dismiss(toastId);
  }

  /**
   * Dismiss all toasts in group
   * @param {string} group
   */
  dismissGroup(group) {
    if (!isUndefined(group) && this.toastsGroups[group]) {
      this.toastsGroups[group].forEach(toast.dismiss);
      delete this.toastsGroups[group];
    }
  }

  /**
   * Clear waiting notification queue
   * @param [params.containerId] - string
   * @param [params] - params
   */
  clearWaitingQueue(params) {
    toast.clearWaitingQueue(params);
  }
}

export default new ToastService();
